import { isEqual } from 'lodash';
import { type RefObject, useMemo, useRef, useState } from 'react';
import {
  Channels,
  LEGEND_WIDTH
} from '@pasqal/core/ui/components/pulser-studio/components/Channels';
import { ChannelInteractive } from '~/components/channels/Channel';
import { useCurrentFrame } from '~/systems/currentFrame';
import { useExperiment } from '~/systems/experiment';
import { useSimulation } from '~/systems/simulation';
import { useUiState } from '~/systems/uiState';
import { Bounds, keyboardConsumed, usePointer, useWindowEvent } from '~/util';
import { Vec2 } from '~/util/vec2';

interface ChannelsInteractiveProps {
  headerNode: HTMLElement | null;
}

export const ChannelsInteractive = ({
  headerNode
}: ChannelsInteractiveProps) => {
  const { channels, computedPulses, maxTime, openTransaction, onReplace } =
    useExperiment();
  const simulation = useSimulation();
  const currentFrame = useCurrentFrame();
  const uiState = useUiState();
  const channelBodyRefsRef = useRef<RefObject<HTMLElement | null>[]>([]);
  const tlBodyRef = useRef<HTMLDivElement>(null);

  const [timeScale, setTimeScale] = useState(1);
  const [lastKnownScrollPos, setLastKnownScrollPos] = useState({
    x: 0,
    y: 0,
    h: 0
  });

  const [selectionBox, setSelectionBox] = useState<[Vec2, Vec2]>([
    Vec2.zero(),
    Vec2.zero()
  ]);

  const currentTime = currentFrame.currentTime;

  const ticksDone = simulation.progress.ticksDone;

  const pulsesPerChannel = useMemo(() => {
    return channels.map((channel) => ({
      channel,
      pulses: computedPulses.filter((p) => p.pulse.channelUuid === channel.uuid)
    }));
  }, [channels, computedPulses]);

  // Selection box
  const selectionBounds = useMemo(() => {
    const [p1, p2] = selectionBox;
    const x = Math.min(p1.x, p2.x);
    const y = Math.min(p1.y, p2.y);
    const w = Math.abs(p1.x - p2.x);
    const h = Math.abs(p1.y - p2.y);
    return { x, y, w, h };
  }, [selectionBox]);

  const channelVisualBoundsRef = useRef<Bounds[]>([]);
  const shiftAtStartRef = useRef(false);
  const initialSelectionSetRef = useRef(new Set<string>());

  function updateSelection(additive: boolean) {
    const { x, y, w, h } = selectionBounds;
    const timeBounds = {
      min: (x - LEGEND_WIDTH) * timeScale,
      max: (x + w - LEGEND_WIDTH) * timeScale
    };
    const yBounds = { min: y, max: y + h };
    const selected = new Set(uiState.pulseSelection);
    const chs = pulsesPerChannel;
    for (const idx in channelVisualBoundsRef.current) {
      const channelBounds = channelVisualBoundsRef.current[idx];
      const channelIntersects = Bounds.intersects(yBounds, channelBounds);
      const channel = chs[idx];

      for (const pulse of channel.pulses) {
        if (
          channelIntersects &&
          Bounds.intersects(timeBounds, pulse.layout.time)
        ) {
          selected.add(pulse.pulse.uuid);
        } else if (
          !additive ||
          !initialSelectionSetRef.current.has(pulse.pulse.uuid)
        ) {
          selected.delete(pulse.pulse.uuid);
        }
      }
    }

    if (!isEqual(selected, uiState.pulseSelection)) {
      uiState.replacePulseSelection(selected);
    }
  }

  const selectionPointer = usePointer((pos, type, event) => {
    // Pointer down
    if (type === 'start') {
      shiftAtStartRef.current = event.shiftKey;
      setSelectionBox([
        Vec2.sub(pos.initial, pos.bounds),
        Vec2.sub(pos.initial, pos.bounds)
      ]);
      initialSelectionSetRef.current = new Set(uiState.pulseSelection);
      channelVisualBoundsRef.current = [];
      for (const bodyRef of channelBodyRefsRef.current) {
        const node = bodyRef.current;
        if (node) {
          const bounds = node.getBoundingClientRect();
          channelVisualBoundsRef.current.push({
            min: bounds.top - pos.bounds.y,
            max: bounds.top + bounds.height - pos.bounds.y
          });
        }
      }
    }
    setSelectionBox((prev) => [prev[0], Vec2.sub(pos.point, pos.bounds)]);
    updateSelection(shiftAtStartRef.current);
  });

  useWindowEvent('keydown', (event) => {
    if (keyboardConsumed()) {
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      const pulses = uiState.pulseSelection;
      if (pulses.size > 0) {
        const tx = openTransaction('removePulse KB');
        for (const uuid of pulses) {
          tx.push({ kind: 'remove_pulse', uuid });
        }
        tx.commit();
      }
    }
  });

  const tlDragEvents = usePointer((pos) => {
    const timeAtCursor = (pos.point.x - pos.bounds.x) * timeScale;
    currentFrame.setCurrentTime(timeAtCursor);
  });

  const tlDragHandle = usePointer((pos) => {
    if (tlBodyRef.current == null) {
      return;
    }
    const bounds = tlBodyRef.current.getBoundingClientRect();
    const timeAtCursor = (pos.point.x - bounds.x) * timeScale;
    currentFrame.setCurrentTime(timeAtCursor);
  });

  const tlHandlePos = currentTime / timeScale;
  const tlHandleTranslate = `${tlHandlePos}px, 0px`;
  const tlHandleTop = `${30 + lastKnownScrollPos.y}px`;
  const tlClip = `${lastKnownScrollPos.x - tlHandlePos}px`;
  const legendWidthPx = `${LEGEND_WIDTH}px`;

  const tlHandleStyle = {
    top: tlHandleTop,
    left: `calc(${legendWidthPx} - 1px)`,
    clipPath: `inset(-10px -10px -10px ${tlClip})`,
    transform: `translate(${tlHandleTranslate})`
  };

  return (
    <Channels
      headerNode={headerNode}
      channelComponent={ChannelInteractive}
      channelBodyRefsRef={channelBodyRefsRef}
      channels={pulsesPerChannel}
      maxTime={maxTime}
      ticksDone={ticksDone}
      setTimeScale={setTimeScale}
      timeScale={timeScale}
      interactivity={{
        lastKnownScrollPos,
        setLastKnownScrollPos,
        selectionPointer,
        selectionBounds,
        tlBodyRef,
        tlDragEvents,
        tlDragHandle,
        tlHandleStyle
      }}
      onReplace={onReplace}
    />
  );
};
